import React, { useState, useEffect, useRef, useCallback } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import Button from "../Tools/Components/Button";
import VeldAlias from "./Components/VeldAlias";
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

const PromptOptPagina = ({ location }) => {
    const [promptOpt, setPromptOpt] = useState({
        emailVerzender: "",
        emailOntvanger: "",
        modelName: "zai-org/GLM-5.2",
        prompt: "body of text in question begins here:\n{%PDF_READ%}\nthis is the end of the body of text. provide the following fields, from the body of text in question, as json: factuur nummer, factuurdatum (this is the first date in the text), referentie, prijs exclusief btw, prijs inclusief btw, btw nummer\nfor currency values, do not include the currency symbol in the value.\n'factuurdatum' is a date and should be in the format: yyyy-mm-dd\n'referentie' is a number which should start with '702' and is then followed by 5 or 6 random numbers. Do not generate 'Referentie' number, only fill it in if you can find it in the text.\nif you cannot find a field simply define its value as empty quotation marks like this: \"\".",
        fieldAliases: []
    });
    const [editing, setEditing] = useState(false);
    const [creating, setCreating] = useState(false);
    const { t } = useTranslation();
    const axiosRef = useRef(null);

    const initializeComponent = useCallback(() => {
        const httpService = HttpService;
        httpService.configure();
        axiosRef.current = httpService.getAxiosClient();

        if (!window.location.href.endsWith("create")) {
            if (location.promptOpt) {
                setPromptOpt(location.promptOpt);
                return;
            }
        } else {
            setEditing(true);
            setCreating(true);
        }

        const id = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
        if (!Number.isNaN(Number.parseInt(id))) {
            axiosRef.current.get(`${process.env.REACT_APP_INKOFACT_API_URL}prompt/${id}`)
                .then(response => setPromptOpt(response.data))
                .catch(ex => alert(ex));
        }
    }, [location]);

    const handleCreate = () => {
        const axios = axiosRef.current;
        if (!axios) { alert("axios not yet loaded"); return; }
        axios.post(`${process.env.REACT_APP_INKOFACT_API_URL}prompt/single`, promptOpt)
            .then(response => window.location.href = "/PromptOpt/" + response.data)
            .catch(ex => alert(ex));
    };

    const handleEdit = () => {
        const axios = axiosRef.current;
        if (!axios) { alert("axios not yet loaded"); return; }
        if (editing) {
            axios.put(`${process.env.REACT_APP_INKOFACT_API_URL}prompt`, promptOpt)
                .then(() => window.location.reload())
                .catch(error => { alert(error); window.location.reload() });
        }
        setEditing(!editing);
    };

    const handleAddFieldAlias = () => {
        if (!promptOpt) return;
        setPromptOpt(prev => ({
            ...prev,
            fieldAliases: [...prev.fieldAliases, { promptFieldName: "", mkgFieldName: "" }]
        }));
    };

    const handleDeleteFieldAlias = (index) => {
        if (!promptOpt) return;
        setPromptOpt(prev => ({
            ...prev,
            fieldAliases: prev.fieldAliases.filter((_, i) => i !== index)
        }));
    };

    const handleDelete = () => {
        const axios = axiosRef.current;
        if (!axios) { alert("axios not yet loaded"); return; }
        const id = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
        if (Number.isNaN(Number.parseInt(id))) return;
        axios.delete(`${process.env.REACT_APP_INKOFACT_API_URL}prompt/${id}`)
            .then(() => window.location.href = "/PromptOpt")
            .catch(ex => alert(ex));
    };

    const setPromptOptProperty = (propName, propVal) => {
        if (typeof propName !== "string") {
            alert("tried setting a prop on prompt opt with non string key. Key was: " + propName);
            return;
        }
        setPromptOpt(prev => ({ ...prev, [propName]: propVal }));
    };

    const setAliasProperty = (index, propName, val) => {
        if (typeof propName !== "string") {
            alert("tried setting a prop on prompt opt with non string key. Key was: " + propName);
            return;
        }
        setPromptOpt(prev => {
            const updatedAliases = [...prev.fieldAliases];
            updatedAliases[index] = { ...updatedAliases[index], [propName]: val };
            return { ...prev, fieldAliases: updatedAliases };
        });
    };

    const handlePromptChange = (e) => {
        setPromptOpt(prev => ({ ...prev, prompt: e.target.value }));
    };

    useEffect(() => {
        initializeComponent();
    }, [initializeComponent]);


    return (
        <div className="relative size-full" data-name="Content Box">
            <div className="flex flex-col items-center max-w-inherit min-w-inherit size-full">
                <div className="box-border content-stretch flex flex-col items-center max-w-inherit min-w-inherit px-0 py-2 relative size-full">
                    {/* Header */}
                    <div className="bg-[#00aaad] dark:bg-[#008b8e] relative shrink-0 w-full">
                        <div aria-hidden="true" className="absolute border border-gray-400 border-solid inset-0 pointer-events-none" />
                        <div className="flex flex-col items-center justify-center size-full">
                            <div className="box-border content-stretch flex flex-col items-center justify-center px-[530px] py-[10px] relative w-full">
                                <div className="capitalize flex flex-col font-['Inter:Bold',sans-serif] font-bold justify-center leading-[0] not-italic relative shrink-0 text-[20px] text-center tracking-[-0.1px] w-[200px]">
                                    <p className="leading-[1.45]">{t("promptOpt.header")}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] relative shrink-0 w-full">
                        <div aria-hidden="true" className="absolute border border-gray-400 border-solid inset-0 pointer-events-none" />
                        <div className="size-full">
                            <div className="flex gap-[10px] items-start px-[28px] py-[10px] w-full">
                                {/* Left Column */}
                                <div className="flex-1">
                                    <div className="box-border gap-[10px] items-start px-[10px] relative shrink-0 flex flex-col">
                                        {/* Fields Section */}
                                        <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
                                            <div className="content-stretch flex gap-[7px] items-center relative shrink-0 w-full">
                                                <label className="w-[215px] text-right font-bold">{t("promptOpt.senderLabel")}:</label>
                                                <input
                                                    className={`h-[23px] shrink-0 w-[320px] border border-black border-solid ${editing ? "bg-[#ffffff] dark:bg-gray-700" : "bg-[#dedede] dark:bg-gray-800 pointer-events-none"}`}
                                                    value={promptOpt.emailVerzender}
                                                    onChange={e => setPromptOptProperty("emailVerzender", e.target.value)}
                                                />
                                            </div>
                                            <div className="content-stretch flex gap-[7px] items-center relative shrink-0 w-full">
                                                <label className="w-[215px] text-right font-bold">{t("promptOpt.receiverLabel")}:</label>
                                                <input
                                                    className={`h-[23px] shrink-0 w-[320px] border border-black border-solid ${editing ? "bg-[#ffffff] dark:bg-gray-700" : "bg-[#dedede] dark:bg-gray-800 pointer-events-none"}`}
                                                    value={promptOpt.emailOntvanger}
                                                    onChange={e => setPromptOptProperty("emailOntvanger", e.target.value)}
                                                />
                                            </div>
                                            <div className="content-stretch flex gap-[7px] items-center relative shrink-0 w-full">
                                                <label className="w-[215px] text-right font-bold">{t("promptOpt.modelLabel")}:</label>
                                                <input
                                                    className={`h-[23px] shrink-0 w-[320px] border border-black border-solid ${editing ? "bg-[#ffffff] dark:bg-gray-700" : "bg-[#dedede] dark:bg-gray-800 pointer-events-none"}`}
                                                    value={promptOpt.modelName}
                                                    onChange={e => setPromptOptProperty("modelName", e.target.value)}
                                                />
                                            </div>
                                            <div className="flex flex-row items-center gap-[5px] px-[35px]">
                                                <label className="font-bold">{t("promptOpt.textExtractMethodLabel")}: </label>
                                                <Select
                                                    options={["Paragraphs", "Spaces"].map(value => ({ label: value, value }))}
                                                    value={["Paragraphs", "Spaces"].map(value => ({ label: value, value })).find(option => option.value === (promptOpt?.pdfParserMethod || ""))}
                                                    onChange={option => setPromptOptProperty("pdfParserMethod", option.value)}
                                                    className={editing ? "" : "pointer-events-none"}
                                                />
                                            </div>
                                            <div>
                                                {t("promptOpt.aliasTableDesc")}
                                            </div>
                                            <div className="content-stretch flex flex-col gap-[2px] items-end relative shrink-0 w-full">
                                                <div className="content-stretch flex gap-[7px] items-center relative shrink-0 w-full">
                                                    <div className="w-full">{t("promptOpt.aliasAiFieldLabel")}</div>
                                                    <div className="px-[10px] w-full">{t("promptOpt.aliasMkgFieldLabel")}</div>
                                                </div>
                                                {(promptOpt || { emailVerzender: "", emailOntvanger: "", fieldAliases: [] }).fieldAliases.map((fa, index) => (
                                                    <VeldAlias
                                                        key={fa.id || index}
                                                        index={index}
                                                        VeldAlias={fa}
                                                        editing={editing}
                                                        onLeftChanged={val => setAliasProperty(index, "promptFieldName", val)}
                                                        onRightChanged={val => setAliasProperty(index, "mkgFieldName", val)}
                                                        onDeletePressed={() => handleDeleteFieldAlias(index)}
                                                    />
                                                ))}
                                            </div>
                                            {editing && <Button text="+" width="30" height="30" onClick={handleAddFieldAlias} />}
                                        </div>

                                        {/* Prompt Section */}
                                        <label className="mt-[30px]">{t("promptOpt.promptLabel")}:</label>
                                        <div className="bg-white dark:bg-gray-700 h-grow relative shrink-0 w-full">
                                            <div className="overflow-clip rounded-[inherit] size-full">
                                                <textarea
                                                    id="inputTextBox"
                                                    readOnly={!editing}
                                                    className="box-border content-stretch flex flex-col h-[400px] items-start p-[5px] relative w-full bg-transparent"
                                                    value={promptOpt.prompt}
                                                    onChange={handlePromptChange}
                                                />
                                            </div>
                                            <div aria-hidden="true" className="absolute border border-black border-solid inset-0 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column */}
                                <div className="flex-1">
                                    <div className="basis-0 grow min-h-px min-w-[400px] relative shrink-0">
                                        <div className="flex flex-col items-center min-w-inherit size-full">
                                            <div className="box-border content-stretch flex flex-col gap-[50px] items-center min-w-inherit px-[10px] py-0 relative w-full">
                                                {/* Buttons */}
                                                {creating ? (
                                                    <div className="flex gap-[30px] w-full">
                                                        <Button text={t("promptOpt.createBtn")} onClick={handleCreate} />
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-[30px] w-full">
                                                        <Button text={editing ? t("promptOpt.confirmBtn") : t("promptOpt.editBtn")} onClick={handleEdit} />
                                                        {editing && <Button text={t("promptOpt.cancelBtn")} onClick={() => window.location.reload()} />}
                                                        <Button text={t("promptOpt.deleteBtn")} onClick={handleDelete} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromptOptPagina;
